function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  const found = cookies.find(c => c.trim().startsWith(`${name}=`));
  if (!found) return null;
  return decodeURIComponent(found.split('=')[1] ?? '') || null;
}

function writeCookie(
  name: string,
  value: string,
  opts?: {
    domain?: string;
    path?: string;
    expiresISO?: string;
    secure?: boolean;
    sameSite?: 'Lax' | 'Strict' | 'None';
  }
) {
  if (typeof document === 'undefined') return;
  const domain = opts?.domain ?? '.play-studios.ai';
  const path = opts?.path ?? '/';
  let cookie = `${name}=${encodeURIComponent(value)}; domain=${domain}; path=${path}`;
  if (opts?.expiresISO) {
    const expires = new Date(opts.expiresISO).toUTCString();
    cookie += `; expires=${expires}`;
  }
  if (opts?.secure) cookie += '; Secure';
  if (opts?.sameSite) cookie += `; SameSite=${opts.sameSite}`;
  document.cookie = cookie;
}

function deleteCookie(name: string, opts?: { domain?: string; path?: string }) {
  if (typeof document === 'undefined') return;
  const domain = opts?.domain ?? '.play-studios.ai';
  const path = opts?.path ?? '/';
  // Set expiry in the past
  document.cookie = `${name}=; domain=${domain}; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

//
// ---- Access token (existing behaviour) ----
// Access token persists in localStorage under 'playgrid-access-token' and expiry under 'playgrid-token-expiry'.
// Keep the helpers minimal because store code already handles these keys directly.
//

const ACCESS_TOKEN_STORAGE_KEY = 'playgrid-access-token';
const REFRESH_TOKEN_STORAGE_KEY = 'playgrid-refresh-token';
const REFRESH_TOKEN_EXPIRY_STORAGE_KEY = 'playgrid-refresh-token-expiry';

function readAccessTokenFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get('token') || searchParams.get('authToken') || null;
}

function readRefreshTokenFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get('refreshToken') || null;
}

function readRefreshTokenExpiryFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get('refreshTokenExpires') || null;
}

/** Read access token from URL query params only. */
export function getAuthTokenFromUrl(): string | null {
  return readAccessTokenFromUrl();
}

/** Priority: URL query param, then cookie, then localStorage. */
export function getAuthToken(): string | null {
  const urlToken = readAccessTokenFromUrl();
  if (urlToken) return urlToken;

  const cookieToken = readCookie('authToken');
  if (cookieToken) return cookieToken;

  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = window.localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
      if (stored) return stored;
    }
  } catch {
    // ignore localStorage failures
  }

  return null;
}

export function cleanAuthTokenFromUrl(): void {
  if (typeof window === 'undefined') {
    return;
  }
  const url = new URL(window.location.href);
  if (url.searchParams.has('token') || url.searchParams.has('authToken')) {
    url.searchParams.delete('token');
    url.searchParams.delete('authToken');
    window.history.replaceState({}, '', url.toString());
  }
}

//
// ---- Refresh token helpers (NEW) ----
// Read and write refresh token similar to auth token. Persist to cookie (primary) and fallback localStorage.
//

/** Priority: URL query param, then cookie, then localStorage. */
export function getRefreshToken(): string | null {
  const urlToken = readRefreshTokenFromUrl();
  if (urlToken) return urlToken;

  const cookieVal = readCookie('refreshToken');
  if (cookieVal) return cookieVal;

  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = window.localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
      if (stored) return stored;
    }
  } catch {
    // ignore localStorage failures
  }

  return null;
}

/** Priority: URL query param, then cookie, then localStorage. */
export function getRefreshTokenExpiry(): string | null {
  const urlExpiry = readRefreshTokenExpiryFromUrl();
  if (urlExpiry) return urlExpiry;

  const cookieVal = readCookie('refreshTokenExpires');
  if (cookieVal) return cookieVal;

  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = window.localStorage.getItem(
        REFRESH_TOKEN_EXPIRY_STORAGE_KEY
      );
      if (stored) return stored;
    }
  } catch {
    // ignore localStorage failures
  }

  return null;
}

/**
 * Persist refresh token to cookie (and localStorage fallback).
 * You can pass an optional ISO expiry string which will be stored as cookie expiry.
 */
export function setRefreshToken(
  refreshToken: string,
  expiresISO?: string | null
): void {
  if (typeof document === 'undefined') return;
  // write cookie (primary)
  writeCookie('refreshToken', refreshToken, {
    domain: '.play-studios.ai',
    path: '/',
    expiresISO: expiresISO ?? undefined,
    secure: true,
    sameSite: 'Lax',
  });

  try {
    // fallback to localStorage for environments that cannot access cookies (rare)
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('playgrid-refresh-token', refreshToken);
      if (expiresISO) {
        window.localStorage.setItem(
          'playgrid-refresh-token-expiry',
          expiresISO
        );
      }
    }
  } catch {
    // ignore localStorage failures
    // console.warn('Unable to persist refresh token to localStorage', e);
  }
}

/**
 * Persist refresh token expiry (ISO string) in cookie + localStorage
 */
export function setRefreshTokenExpiry(expiryISO: string): void {
  if (typeof document === 'undefined') return;
  writeCookie('refreshTokenExpires', expiryISO, {
    domain: '.play-studios.ai',
    path: '/',
    expiresISO: expiryISO,
    secure: true,
    sameSite: 'Lax',
  });

  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('playgrid-refresh-token-expiry', expiryISO);
    }
  } catch {
    // ignore
  }
}

/**
 * Remove persisted refresh token (cookie + localStorage)
 */
export function removeRefreshToken(): void {
  if (typeof document !== 'undefined') {
    deleteCookie('refreshToken', { domain: '.play-studios.ai', path: '/' });
    deleteCookie('refreshTokenExpires', {
      domain: '.play-studios.ai',
      path: '/',
    });
  }

  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem('playgrid-refresh-token');
      window.localStorage.removeItem('playgrid-refresh-token-expiry');
    }
  } catch {
    // ignore
  }
}

/**
 * Remove any auth tokens from URL (token / authToken / refreshToken / refreshTokenExpires)
 */
export function cleanTokensFromUrl(): void {
  if (typeof window === 'undefined') {
    return;
  }
  const url = new URL(window.location.href);
  ['token', 'authToken', 'refreshToken', 'refreshTokenExpires'].forEach(k => {
    if (url.searchParams.has(k)) url.searchParams.delete(k);
  });
  window.history.replaceState({}, '', url.toString());
}
